import {
    formatISO,
    getMonth,
    getQuarter,
    getYear,
    isAfter,
    isBefore,
    parseISO,
    startOfDay,
    startOfWeek,
    subDays,
} from "date-fns";
import { ServiceTemplateRecurrence } from "@common/schemas/serviceTemplateRecurrence.schema";
import {
    getFirstServiceStart,
    getNextServiceStart,
} from "@common/helpers/serviceTemplateRecurrenceServiceDates";
import {
    getApplicationDate,
    getWorkEndDate,
    getWorkStartDate,
} from "@common/helpers/serviceTemplateRecurrenceWorkDates";
import { getOccurrencePeriodCap } from "@common/helpers/serviceTemplateRecurrenceOccurrenceCaps";
import { ServiceTemplateRecurrencePeriod } from "@common/types/serviceTemplateRecurrencePeriod";

export interface ScheduledOccurrence {
    index: number;
    applicationDate: Date;
    serviceStartDate: Date;
    serviceEndDate: Date;
    workStartDate: Date;
    workEndDate: Date;
}

interface GenerateServiceTemplateScheduleOptions {
    recurrence: ServiceTemplateRecurrence;
    fromDate?: Date;
    count?: number;
    firstWorkStartDate?: Date | null;
    today?: Date;
}

function getPeriodBucketKey(
    date: Date,
    period: ServiceTemplateRecurrencePeriod,
): string {
    switch (period) {
        case ServiceTemplateRecurrencePeriod.DAILY:
            return formatISO(date, { representation: "date" });
        case ServiceTemplateRecurrencePeriod.WEEKLY:
            return formatISO(startOfWeek(date, { weekStartsOn: 0 }), {
                representation: "date",
            });
        case ServiceTemplateRecurrencePeriod.MONTHLY:
            return `${getYear(date)}-${getMonth(date)}`;
        case ServiceTemplateRecurrencePeriod.QUARTERLY:
            return `${getYear(date)}-Q${getQuarter(date)}`;
        case ServiceTemplateRecurrencePeriod.ANNUALLY:
            return `${getYear(date)}`;
    }
}

export function generateServiceTemplateSchedule({
    recurrence,
    fromDate = new Date(),
    count,
    firstWorkStartDate = null,
    today = startOfDay(new Date()),
}: GenerateServiceTemplateScheduleOptions): ScheduledOccurrence[] {
    if (count !== undefined && count <= 0) {
        return [];
    }

    const interval = Math.max(1, recurrence.interval);
    const periodCap = getOccurrencePeriodCap(recurrence.period, interval);
    const effectiveCount =
        count === undefined ? periodCap : Math.min(count, periodCap);

    const results: ScheduledOccurrence[] = [];
    const recurrenceEndDate = recurrence.endDate
        ? parseISO(recurrence.endDate)
        : null;
    const generatedUntil = recurrence.generatedUntil
        ? parseISO(recurrence.generatedUntil)
        : null;
    const maximumOccurrenceCount =
        recurrence.maxOccurrenceCount ?? Number.POSITIVE_INFINITY;
    // Always walk every base period; interval only decides which buckets to keep.
    let serviceStartDate = getFirstServiceStart(recurrence);
    let emitIndex = 0;
    let bucketIndex = -1;
    let lastBucketKey: string | null = null;
    let scans = 0;
    const maxScans = Math.max(periodCap * interval * 4, effectiveCount * 4);

    while (emitIndex < maximumOccurrenceCount && scans < maxScans) {
        scans += 1;
        if (recurrenceEndDate && isAfter(serviceStartDate, recurrenceEndDate)) {
            break;
        }

        const bucketKey = getPeriodBucketKey(
            serviceStartDate,
            recurrence.period,
        );
        if (bucketKey !== lastBucketKey) {
            bucketIndex += 1;
            lastBucketKey = bucketKey;
        }

        const nextServiceStartDate = getNextServiceStart({
            current: serviceStartDate,
            recurrence,
        });
        const serviceEndDate = subDays(nextServiceStartDate, 1);
        const isKeptBucket = bucketIndex % interval === 0;

        if (!isKeptBucket) {
            serviceStartDate = nextServiceStartDate;
            continue;
        }

        const isExplicitFirstWorkStart =
            emitIndex === 0 && firstWorkStartDate != null;
        const workStartDate = isExplicitFirstWorkStart
            ? startOfDay(firstWorkStartDate)
            : getWorkStartDate({ serviceEndDate, recurrence });
        const applicationDate = getApplicationDate({
            workStartDate,
            recurrence,
        });

        if (isAfter(applicationDate, fromDate)) {
            break;
        }

        // Past-due applications apply immediately — never store/show a date
        // before today.
        const effectiveApplicationDate = isBefore(applicationDate, today)
            ? today
            : applicationDate;

        // Work cannot start before today either — a past-due work start would
        // otherwise leave the occurrence in a state where both work start and
        // end already elapsed before the schedule was even applied. An
        // explicit first-occurrence work start date (used to backfill
        // historical/in-progress work at save time) is intentionally exempt —
        // the caller chose that date on purpose.
        const effectiveWorkStartDate =
            !isExplicitFirstWorkStart && isBefore(workStartDate, today)
                ? today
                : workStartDate;

        if (!generatedUntil || isAfter(serviceStartDate, generatedUntil)) {
            results.push({
                index: emitIndex,
                applicationDate: effectiveApplicationDate,
                serviceStartDate,
                serviceEndDate,
                workStartDate: effectiveWorkStartDate,
                workEndDate: getWorkEndDate({
                    workStartDate: effectiveWorkStartDate,
                    recurrence,
                }),
            });
        }

        if (results.length >= effectiveCount) {
            break;
        }

        serviceStartDate = nextServiceStartDate;
        emitIndex += 1;
    }

    return results;
}
